// W36: thin wrapper over the generated media client.
//
// W18 / W40: typed access to services/media.
//
//   POST   /v1/media                     multipart upload (scan → store → extract)
//   GET    /v1/media                     list this tenant's objects, paged
//   GET    /v1/media/{id}                the original bytes
//   GET    /v1/media/{id}/meta           the row, with a signed URL
//   GET    /v1/media/{id}/url            a fresh signed URL on its own
//   GET    /v1/media/{id}/text           the extracted text
//   PATCH  /v1/media/{id}                rename
//   DELETE /v1/media/{id}                remove object + row
//   GET  /v1/admin/stats/media           platform-admin storage stats
//   POST /v1/admin/media/{id}/extract    platform-admin re-extract
//   GET  /v1/admin/media/scans/summary   platform-admin scan verdict counts
//   GET  /v1/admin/media/scans          platform-admin scan log
//
// Types come from `./generated/media` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate media`); the request path
// stays on the house core so the envelope, Idempotency-Key, token refresh and
// ApiError mapping behave exactly as before: except `download`, which reads
// binary and deliberately bypasses the pipeline (see below).
//
// Uploads are virus-scanned with ClamAV BEFORE anything is persisted, so a
// rejected upload leaves no row and no stored object: see `MediaErrorCode`.

import { http } from './_core/request.js';
import { getConfig } from './_core/config.js';
import { readAccessToken } from './_core/tokens.js';
import { ApiError, NetworkError } from './_core/errors.js';
import type { Envelope } from './_core/envelope.js';
import type * as gen from './generated/media/index.js';

// ── Errors ───────────────────────────────────────────────────────────────

/**
 * Error codes the media endpoints return, in the standard `ApiError.code`
 * position. Catch with `isApiError(e) && e.code === 'INFECTED'`.
 *
 * Hand-written: the service returns these as `error.code` strings inside the
 * house error envelope, and OpenAPI carries no enumeration of them.
 *
 * - `INFECTED` - 422. ClamAV matched a signature; `ApiError.body.signature`
 *   carries the signature name. Nothing was stored.
 * - `SCAN_UNAVAILABLE`: 503. Scanning is required but clamd is unreachable.
 *   Fail-closed: retry once the scanner is back.
 * - `SCAN_NOT_CONFIGURED`: 503. A Production deployment has
 *   `Media:ScanRequired=false`. An operator must enable scanning.
 * - `TOO_LARGE` - 413. Over `Media:MaxUploadBytes`.
 * - `MISSING_FILE` - 400. Multipart body with no `file` part.
 * - `NO_TEXT`: 400. The object has no extracted text (wrong MIME).
 * - `MIME_NOT_EXTRACTABLE`: 400. Re-extract asked for on a binary MIME.
 * - `MEDIA_NOT_FOUND`: 404.
 * - `not_configured` - 503. The `aws_s3` integration is unset in Production.
 */
export type MediaErrorCode =
  | 'INFECTED'
  | 'SCAN_UNAVAILABLE'
  | 'SCAN_NOT_CONFIGURED'
  | 'TOO_LARGE'
  | 'MISSING_FILE'
  | 'NO_TEXT'
  | 'MIME_NOT_EXTRACTABLE'
  | 'MEDIA_NOT_FOUND'
  | 'not_configured';

/**
 * Body of a 422 `INFECTED` response: the one media error that is not the house
 * error envelope. `error` is pinned to the single value the service sends; the
 * generated `InfectedUploadResponse` types it as an open `string`.
 */
export type InfectedErrorBody = Omit<gen.InfectedUploadResponse, 'error'> & {
  error: 'INFECTED';
};

// ── Types (generated) ────────────────────────────────────────────────────

/**
 * A stored object as `POST /v1/media` returns it. `extracted_present` is true
 * when the service extracted text at upload time (text/*, PDF, HTML);
 * `scanned` is true when ClamAV scanned the payload clean; false means the
 * scan was skipped, only possible outside Production with
 * `Media:ScanRequired=false`.
 */
export type MediaObject = gen.MediaUploadDto;

/**
 * A stored object as the list and metadata endpoints return it, carrying a
 * signed `url` that anyone holding it can GET until `url_expires_at`.
 *
 * That URL is the storable one. `downloadUrl` needs a bearer token, so it
 * cannot go in an `<img src>`, cannot be handed to Meta for a template's media
 * header (Meta fetches it anonymously), and cannot be emailed.
 */
export type StoredMedia = gen.MediaObjectDto;

/** A signed URL on its own, from {@link signedUrl}. */
export type MediaUrl = gen.MediaUrlDto;

/** One page of {@link list}. */
export type MediaListMeta = gen.ListMeta;

export type MediaText = gen.MediaTextDto;
export type MediaStatsByMime = gen.MediaStatsByMimeDto;
export type MediaStatsByTenant = gen.MediaStatsByTenantDto;

/**
 * Platform-admin storage stats.
 *
 * NOTE: `admin.MediaStats` in `./admin.ts` is a *different*, narrower type for
 * the same `GET /v1/admin/stats/media` payload: it omits `uploads_last_24h`,
 * `bytes_last_24h` and `by_mime`. This one matches the service. That module is
 * out of scope for this cutover; the contradiction is recorded in the W36 note.
 */
export type MediaStats = gen.MediaStatsDto;

export type MediaExtractResult = gen.MediaExtractResultDto;

/** House type: options for {@link upload}, not part of any wire schema. */
export interface UploadOptions {
  /** Overrides the filename sent in the multipart part. */
  filename?: string;
  signal?: AbortSignal;
  /** Large uploads need more than the default client timeout. */
  timeoutMs?: number;
  /**
   * Called with 0-100 as the bytes go out. Supplying this switches the request
   * to `XMLHttpRequest`: `fetch` cannot report upload progress in any browser,
   * so a 60MB video would otherwise sit at "uploading" with nothing moving.
   *
   * That path deliberately bypasses the shared pipeline, exactly as
   * {@link download} does, so automatic 401-refresh and retries do not apply:
   * refresh first, then call. Error bodies are still mapped to `ApiError`.
   */
  onProgress?: (percent: number) => void;
}

/** House type: query for {@link list}. */
export interface ListOptions {
  /** Case-insensitive match on the original filename. */
  search?: string;
  /** MIME prefix, so `image/` matches every image. */
  mime?: string;
  /** Default 50, capped at 200 by the service. */
  limit?: number;
  offset?: number;
  /**
   * Lifetime of the signed URL on each row, in seconds. Default 3600, clamped
   * by the service to between 60 and seven days.
   */
  urlTtlSeconds?: number;
  signal?: AbortSignal;
}

// ── Calls ────────────────────────────────────────────────────────────────

/**
 * Upload a file. The payload is virus-scanned before it is stored, so a
 * rejected upload creates nothing: catch `INFECTED` (422) and surface the
 * signature to the user rather than retrying.
 *
 * Pass a `File`/`Blob` in the browser, or a `Blob` built from bytes in Node.
 */
export function upload(file: Blob, opts?: UploadOptions): Promise<Envelope<MediaObject>> {
  const form = new FormData();
  const name = opts?.filename ?? (file instanceof File ? file.name : 'upload.bin');
  form.append('file', file, name);
  if (opts?.onProgress) return uploadWithProgress(form, opts);
  return http.post<Envelope<MediaObject>>('/v1/media', {
    body: form,
    signal: opts?.signal,
    timeoutMs: opts?.timeoutMs
  });
}

/** The XHR path behind {@link UploadOptions.onProgress}. */
function uploadWithProgress(form: FormData, opts: UploadOptions): Promise<Envelope<MediaObject>> {
  const cfg = getConfig();
  const token = readAccessToken();

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${cfg.apiBaseUrl}/v1/media`);
    xhr.responseType = 'text';
    xhr.withCredentials = cfg.http.credentials === 'include';
    if (opts.timeoutMs) xhr.timeout = opts.timeoutMs;
    xhr.setRequestHeader('accept', 'application/json');
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    // Content-Type is deliberately unset: the browser adds the multipart
    // boundary, and setting it by hand produces a body the server cannot parse.

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress!(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* non-JSON body */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        // The bytes are through; the reported percentage should say so even if
        // the last progress event never arrived.
        opts.onProgress!(100);
        resolve(body as Envelope<MediaObject>);
        return;
      }
      reject(toUploadError(xhr.status, body));
    };

    xhr.onerror = () => reject(new NetworkError('Upload failed'));
    xhr.ontimeout = () => reject(new NetworkError('Upload timed out'));
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
    if (opts.signal) {
      if (opts.signal.aborted) { xhr.abort(); return; }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(form);
  });
}

/**
 * Map an upload failure onto `ApiError`. The upload endpoint uses two error
 * shapes: the house envelope, and the flat `{ error: 'INFECTED', signature }`
 * a virus hit returns. Both must arrive as a readable `code`.
 */
function toUploadError(status: number, body: unknown): ApiError {
  const b = body as { error?: unknown; message?: string } | null;
  const err = b?.error;
  if (typeof err === 'string') return new ApiError(status, err, b?.message ?? err, body);
  if (err && typeof err === 'object') {
    const nested = err as { code?: string; message?: string };
    return new ApiError(status, nested.code ?? 'upload_failed', nested.message ?? `POST /v1/media → ${status}`, body);
  }
  return new ApiError(status, 'upload_failed', `POST /v1/media → ${status}`, body);
}

/**
 * One page of this tenant's stored files, newest first. Every row carries a
 * signed URL, so a picker can render thumbnails without a request per row.
 */
export function list(opts: ListOptions = {}): Promise<Envelope<StoredMedia[]> & { meta?: MediaListMeta | null }> {
  const q = new URLSearchParams();
  if (opts.search) q.set('search', opts.search);
  if (opts.mime) q.set('mime', opts.mime);
  if (opts.limit != null) q.set('limit', String(opts.limit));
  if (opts.offset != null) q.set('offset', String(opts.offset));
  if (opts.urlTtlSeconds != null) q.set('url_ttl', String(opts.urlTtlSeconds));
  const qs = q.toString();
  return http.get(`/v1/media${qs ? `?${qs}` : ''}`, { signal: opts.signal });
}

/**
 * One object's row, with a freshly signed URL. Use this to refresh a URL that
 * has expired since it was stored, rather than re-uploading the file.
 */
export function get(id: string, urlTtlSeconds?: number): Promise<Envelope<StoredMedia>> {
  const qs = urlTtlSeconds != null ? `?url_ttl=${urlTtlSeconds}` : '';
  return http.get<Envelope<StoredMedia>>(`/v1/media/${encodeURIComponent(id)}/meta${qs}`);
}

/**
 * A fresh signed URL on its own, without the rest of the row.
 *
 * Signed URLs expire by design, so a URL stored months ago in a template or a
 * broadcast will eventually 403. Re-sign from the id rather than storing a URL
 * that never expires: a permanent URL to a private bucket is a leak that
 * cannot be revoked.
 */
export function signedUrl(id: string, ttlSeconds?: number): Promise<Envelope<MediaUrl>> {
  const qs = ttlSeconds != null ? `?ttl=${ttlSeconds}` : '';
  return http.get<Envelope<MediaUrl>>(`/v1/media/${encodeURIComponent(id)}/url${qs}`);
}

/** Change the display filename. The stored object and its URL do not move. */
export function rename(id: string, originalName: string): Promise<Envelope<{ id: string; original_name: string }>> {
  return http.patch<Envelope<{ id: string; original_name: string }>>(
    `/v1/media/${encodeURIComponent(id)}`,
    { body: { original_name: originalName } satisfies gen.RenameMediaRequest }
  );
}

/**
 * Delete the object and its row. Irreversible, and any URL already signed for
 * it stops working. Anything still referencing the file (a template header, a
 * queued broadcast) breaks, so check before calling.
 */
export function remove(id: string): Promise<void> {
  return http.delete<void>(`/v1/media/${encodeURIComponent(id)}`);
}

/**
 * Metadata + extracted text for one object. The media service has no metadata
 * -only endpoint, so this is the text accessor; it fails with 400 `NO_TEXT`
 * when the object's MIME is not extractable.
 */
export function getText(id: string): Promise<Envelope<MediaText>> {
  return http.get<Envelope<MediaText>>(`/v1/media/${encodeURIComponent(id)}/text`);
}

/**
 * Gateway-relative path that streams the original bytes. Feed it to an
 * `<img src>` / `<a href>` after prefixing your API base URL, or fetch it
 * yourself when you need the bearer token on the request.
 */
export function downloadUrl(id: string): string {
  return `/v1/media/${encodeURIComponent(id)}`;
}

/**
 * Fetch the original bytes as a Blob, with the bearer token attached.
 *
 * Hand-typed on purpose: `GET /v1/media/{id}` streams raw bytes and has no JSON
 * schema, so the generated operation (named `media_getMetadata`: a misnomer,
 * it returns the file, not metadata) declares its 200 body as `unknown`.
 *
 * Deliberately bypasses the shared request pipeline: that pipeline reads every
 * response as text and tries to JSON-parse it, which corrupts binary payloads.
 * Retries and 401-refresh do not apply here: re-call after refreshing.
 */
export async function download(id: string, signal?: AbortSignal): Promise<Blob> {
  const cfg = getConfig();
  const fetchImpl = cfg.fetchImpl ?? globalThis.fetch;
  const token = readAccessToken();
  const headers: Record<string, string> = { accept: '*/*' };
  if (token) headers.authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetchImpl(`${cfg.apiBaseUrl}${downloadUrl(id)}`, {
      method: 'GET',
      headers,
      credentials: cfg.http.credentials,
      signal
    });
  } catch (e) {
    throw new NetworkError((e as Error).message ?? 'Network request failed', e);
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, 'media_download_failed', `GET ${downloadUrl(id)} → ${res.status}`, body);
  }
  return res.blob();
}

// ── Admin (platform scope) ───────────────────────────────────────────────

/**
 * Platform-admin storage stats: totals, last-24h uploads, by MIME, by tenant.
 * The service also routes `/v1/admin/media/stats` as an alias for the same
 * payload; this module keeps calling the canonical path.
 */
export function stats(): Promise<Envelope<MediaStats>> {
  return http.get<Envelope<MediaStats>>('/v1/admin/stats/media');
}

/**
 * Re-run text extraction on an already-stored object: after an extractor
 * change, or for rows uploaded before extraction shipped. Platform-admin only.
 */
export function extract(id: string): Promise<Envelope<MediaExtractResult>> {
  return http.post<Envelope<MediaExtractResult>>(
    `/v1/admin/media/${encodeURIComponent(id)}/extract`
  );
}

// ── Admin: virus scan log ────────────────────────────────────────────────
//
// Hand-typed rather than generated: `src/generated/media` is one regeneration
// behind these two routes (`pnpm --filter @repletoryreach/sdk-api generate media`).

/** What the scanner decided about one upload payload. */
export type MediaScanVerdict = 'clean' | 'infected' | 'unscanned';

/** One day of the scan log, oldest first. */
export interface MediaScanDay {
  day: string;
  clean: number;
  infected: number;
  unscanned: number;
}

/** One clamd signature and how often it matched in the window. */
export interface MediaScanSignature {
  signature: string;
  count: number;
}

/**
 * Scan verdicts over a window.
 *
 * `infected` counts uploads that were refused, and a refused upload leaves no
 * stored object and no `media_objects` row, so this is the only place those
 * attempts are counted at all. `unscanned` counts files that were stored while
 * the scanner was unreachable, which a Production deployment cannot reach:
 * it refuses uploads instead.
 *
 * `first_event_at` is where the record starts. The log is not backfilled, so
 * an upload from before that instant is in none of these counts.
 */
export interface MediaScanSummary {
  window: string;
  since: string;
  until: string;
  clean: number;
  infected: number;
  unscanned: number;
  total: number;
  /** Infected as a percentage of the window, to two decimals. */
  infected_rate: number;
  bytes_scanned: number;
  organisations: number;
  first_event_at: string | null;
  last_event_at: string | null;
  by_day: MediaScanDay[];
  top_signatures: MediaScanSignature[];
}

/** One row of the scan log. `media_id` is null when nothing was stored. */
export interface MediaScanEvent {
  id: string;
  organisation_id: string;
  media_id: string | null;
  verdict: MediaScanVerdict;
  /** The clamd signature name, on an infected row. */
  signature: string | null;
  /** Why the scanner could not answer, on an unscanned row. */
  detail: string | null;
  mime_type: string | null;
  size_bytes: number;
  original_name: string | null;
  scanned_at: string;
  created_by: string | null;
}

/** Named windows {@link scanSummary} understands. */
export type MediaScanWindow = '24h' | '7d' | '30d' | '90d';

export interface ScanSummaryOpts {
  window?: MediaScanWindow;
  /** Overrides `window`. ISO 8601. */
  since?: string;
  /** Overrides `window`. ISO 8601. */
  until?: string;
}

/**
 * Scan verdict counts over a window, with a per-day series and the signatures
 * that matched. Platform-admin only.
 */
export function scanSummary(opts?: ScanSummaryOpts): Promise<Envelope<MediaScanSummary>> {
  const query: Record<string, string | undefined> = {};
  if (opts?.window) query.window = opts.window;
  if (opts?.since) query.since = opts.since;
  if (opts?.until) query.until = opts.until;
  return http.get<Envelope<MediaScanSummary>>('/v1/admin/media/scans/summary', { query });
}

export interface ScanListOpts {
  verdict?: MediaScanVerdict;
  org_id?: string;
  since?: string;
  until?: string;
  /** 1 to 500; defaults to 100. */
  limit?: number;
  offset?: number;
}

/** Window metadata on {@link listScans}. */
export interface MediaScanListMeta {
  total: number;
  returned: number;
  capped: boolean;
}

/**
 * The scan log, newest first. A row with a null `media_id` is an upload that
 * was refused, so nothing was stored. Platform-admin only.
 */
export function listScans(
  opts?: ScanListOpts
): Promise<{ data: MediaScanEvent[]; meta: MediaScanListMeta }> {
  const query: Record<string, string | number | undefined> = {};
  if (opts?.verdict) query.verdict = opts.verdict;
  if (opts?.org_id) query.org_id = opts.org_id;
  if (opts?.since) query.since = opts.since;
  if (opts?.until) query.until = opts.until;
  if (opts?.limit !== undefined) query.limit = opts.limit;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  return http.get<{ data: MediaScanEvent[]; meta: MediaScanListMeta }>('/v1/admin/media/scans', { query });
}
